import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { 
  ArrowLeft, RefreshCw, CheckCircle2, XCircle, AlertCircle, 
  Cookie, Code, Send, Bug, Loader2, Eye, EyeOff, Wifi, WifiOff,
  Activity, Globe, Zap
} from 'lucide-react';

const META_PIXEL_ID = '1289244013192694';
const FBEVENTS_URL = 'https://connect.facebook.net/en_US/fbevents.js';

// Status icon component
const StatusIcon = ({ ok, warning }) => {
  if (warning) return <AlertCircle className="w-5 h-5 text-amber-500" />;
  return ok 
    ? <CheckCircle2 className="w-5 h-5 text-green-500" /> 
    : <XCircle className="w-5 h-5 text-red-500" />;
};

const MetaDebugPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [showRawData, setShowRawData] = useState(false);
  
  // Debug states
  const [debugState, setDebugState] = useState({
    marketingConsent: false,
    fbqExists: false,
    fbqInitialized: false,
    scriptLoaded: false,
    scriptLoadError: null,
    actualPixelId: null,
    pixelIdMatch: false,
  });
  
  // Network tracking
  const [networkRequests, setNetworkRequests] = useState([]);
  const [fbqCalls, setFbqCalls] = useState([]);
  const [errors, setErrors] = useState([]);
  const [rawData, setRawData] = useState({});
  
  // Conversion events log (from metaPixel.js global log)
  const [conversionEvents, setConversionEvents] = useState([]);
  
  // Ref to track if we've set up interception
  const interceptorSetup = useRef(false);
  
  // Listen for conversion events from metaPixel.js
  useEffect(() => {
    // Load existing events
    if (window.__metaPixelEventLog) {
      setConversionEvents([...window.__metaPixelEventLog]);
    }
    
    // Listen for new events
    const handleMetaEvent = (e) => {
      setConversionEvents(prev => [...prev, e.detail]);
    };
    
    window.addEventListener('metaPixelEvent', handleMetaEvent);
    
    return () => {
      window.removeEventListener('metaPixelEvent', handleMetaEvent);
    };
  }, []);

  // Setup fbq interception - safer approach using proxy on queue
  const setupFbqInterceptor = useCallback(() => {
    if (interceptorSetup.current) return;
    interceptorSetup.current = true;
    
    console.log('[MetaDebug] Setting up fbq monitoring...');
    
    // Check periodically for fbq calls instead of intercepting
    // This is safer and doesn't interfere with Meta's script
    const checkFbq = () => {
      try {
        if (window.fbq && window.fbq.queue) {
          const queue = window.fbq.queue;
          // Handle both array and array-like objects
          const queueArray = Array.isArray(queue) ? queue : Array.from(queue);
          
          queueArray.forEach((args) => {
            if (args && args[0]) {
              // Convert Arguments object to array safely
              const argsArray = Array.isArray(args) ? args : Array.from(args);
              const callInfo = {
                timestamp: new Date().toISOString(),
                method: argsArray[0],
                args: argsArray.slice(1),
                argsString: JSON.stringify(argsArray),
                source: 'queue'
              };
              
              // Check for init to get pixel ID
              if (argsArray[0] === 'init' && argsArray[1]) {
                setDebugState(prev => ({
                  ...prev,
                  actualPixelId: argsArray[1],
                  pixelIdMatch: argsArray[1] === META_PIXEL_ID
                }));
              }
              
              setFbqCalls(prev => {
                // Avoid duplicates
                const exists = prev.some(c => c.argsString === callInfo.argsString);
                if (exists) return prev;
                return [...prev, callInfo];
              });
            }
          });
        }
      } catch (e) {
        // Silently ignore - don't spam console
      }
    };
    
    // Run check periodically
    const intervalId = setInterval(checkFbq, 1000);
    
    // Cleanup on unmount will happen through React's effect cleanup
    window.__metaDebugInterval = intervalId;
    
    console.log('[MetaDebug] fbq monitoring set up');
  }, []);

  // Monitor network requests to facebook.com/tr
  const setupNetworkMonitor = useCallback(() => {
    console.log('[MetaDebug] Setting up network monitor...');
    
    // Monitor fetch
    const originalFetch = window.fetch;
    window.fetch = function(...args) {
      const url = args[0]?.toString() || '';
      if (url.includes('facebook.com') || url.includes('facebook.net')) {
        const reqInfo = {
          timestamp: new Date().toISOString(),
          type: 'fetch',
          url: url.substring(0, 200),
          method: args[1]?.method || 'GET'
        };
        console.log('[MetaDebug] Network request:', reqInfo);
        setNetworkRequests(prev => [...prev, reqInfo]);
      }
      return originalFetch.apply(this, args);
    };
    
    // Monitor XMLHttpRequest
    const originalXHROpen = XMLHttpRequest.prototype.open;
    XMLHttpRequest.prototype.open = function(method, url, ...rest) {
      if (url && (url.includes('facebook.com') || url.includes('facebook.net'))) {
        const reqInfo = {
          timestamp: new Date().toISOString(),
          type: 'xhr',
          url: url.toString().substring(0, 200),
          method: method
        };
        console.log('[MetaDebug] XHR request:', reqInfo);
        setNetworkRequests(prev => [...prev, reqInfo]);
      }
      return originalXHROpen.call(this, method, url, ...rest);
    };
    
    // Monitor network using PerformanceObserver (safer than overriding Image)
    try {
      const observer = new PerformanceObserver((list) => {
        for (const entry of list.getEntries()) {
          if (entry.name && (entry.name.includes('facebook.com') || entry.name.includes('facebook.net'))) {
            const reqInfo = {
              timestamp: new Date().toISOString(),
              type: 'resource',
              url: entry.name.substring(0, 300),
              method: 'GET',
              duration: Math.round(entry.duration) + 'ms'
            };
            console.log('[MetaDebug] Resource loaded:', reqInfo);
            setNetworkRequests(prev => {
              // Avoid duplicates
              if (prev.some(r => r.url === reqInfo.url && r.timestamp === reqInfo.timestamp)) {
                return prev;
              }
              return [...prev, reqInfo];
            });
          }
        }
      });
      observer.observe({ entryTypes: ['resource'] });
      console.log('[MetaDebug] PerformanceObserver installed');
    } catch (e) {
      console.log('[MetaDebug] PerformanceObserver not supported:', e);
    }
    
    console.log('[MetaDebug] Network monitor installed');
  }, []);

  // Check fbevents.js loading status
  const checkScriptStatus = useCallback(() => {
    return new Promise((resolve) => {
      // Check if script already in DOM
      const existingScript = document.querySelector(`script[src*="fbevents.js"]`);
      if (existingScript) {
        resolve({ loaded: true, error: null });
        return;
      }
      
      // Try to load it manually and check
      const testScript = document.createElement('script');
      testScript.src = FBEVENTS_URL;
      testScript.async = true;
      
      testScript.onload = () => {
        console.log('[MetaDebug] fbevents.js loaded successfully');
        resolve({ loaded: true, error: null });
      };
      
      testScript.onerror = (e) => {
        console.error('[MetaDebug] fbevents.js failed to load:', e);
        resolve({ loaded: false, error: 'Script failed to load - check ad blockers' });
      };
      
      // Don't actually add test script, just check existing
      resolve({ 
        loaded: !!existingScript, 
        error: existingScript ? null : 'Script not in DOM' 
      });
    });
  }, []);

  // Comprehensive debug state refresh
  const refreshDebugState = useCallback(async () => {
    setLoading(true);
    console.log('[MetaDebug] Refreshing debug state...');
    
    try {
      const state = {
        marketingConsent: false,
        fbqExists: false,
        fbqInitialized: false,
        scriptLoaded: false,
        scriptLoadError: null,
        actualPixelId: null,
        pixelIdMatch: false,
      };
      
      // Check consent
      try {
        if (window.__metaPixelConsent === true) {
          state.marketingConsent = true;
        } else {
          const consent = localStorage.getItem('smartballoon-cookie-consent');
          if (consent) {
            const parsed = JSON.parse(consent);
            state.marketingConsent = parsed.marketing === true;
          }
        }
      } catch (e) {
        console.error('[MetaDebug] Consent check error:', e);
      }
      
      // Check fbq
      state.fbqExists = typeof window.fbq === 'function';
      state.fbqInitialized = window.__fbqInitialized === true;
      
      // Check script in DOM
      const scripts = document.querySelectorAll('script[src*="fbevents.js"]');
      state.scriptLoaded = scripts.length > 0;
      
      if (!state.scriptLoaded) {
        state.scriptLoadError = 'fbevents.js not found in DOM - may be blocked by ad blocker';
      }
      
      // Try to get actual Pixel ID from fbq internals
      if (window.fbq) {
        // Method 1: Check fbq.getState if available
        if (typeof window.fbq.getState === 'function') {
          try {
            const fbqState = window.fbq.getState();
            console.log('[MetaDebug] fbq state:', fbqState);
            if (fbqState?.pixels?.[0]?.id) {
              state.actualPixelId = fbqState.pixels[0].id;
            }
          } catch (e) {
            console.log('[MetaDebug] getState not available');
          }
        }
        
        // Method 2: Check _fbq or fbq internals
        if (!state.actualPixelId && window._fbq) {
          try {
            if (window._fbq.pixelId) {
              state.actualPixelId = window._fbq.pixelId;
            }
          } catch (e) {
            console.log('[MetaDebug] _fbq.pixelId not available');
          }
        }
        
        // Method 3: Check fbq.queue for init calls
        if (!state.actualPixelId && window.fbq.queue) {
          const initCall = window.fbq.queue.find(q => q[0] === 'init');
          if (initCall && initCall[1]) {
            state.actualPixelId = initCall[1];
          }
        }
        
        // Method 4: Look for pixel in global Facebook object
        if (!state.actualPixelId && window.FB?.Pixel?.pixelID) {
          state.actualPixelId = window.FB.Pixel.pixelID;
        }
      }
      
      state.pixelIdMatch = state.actualPixelId === META_PIXEL_ID;
      
      // Gather comprehensive raw data
      const raw = {
        '--- CONSENT ---': '---',
        'window.__metaPixelConsent': window.__metaPixelConsent,
        'window.__cookieConsent': window.__cookieConsent,
        'localStorage consent': null,
        '--- FBQ ---': '---',
        'typeof window.fbq': typeof window.fbq,
        'window.__fbqInitialized': window.__fbqInitialized,
        'window._fbq exists': !!window._fbq,
        'fbq.version': window.fbq?.version,
        'fbq.loaded': window.fbq?.loaded,
        'fbq.queue': window.fbq?.queue,
        'fbq.queue length': window.fbq?.queue?.length || 0,
        '--- PIXEL ID ---': '---',
        'Expected Pixel ID': META_PIXEL_ID,
        'Detected Pixel ID': state.actualPixelId || 'NOT DETECTED',
        'Pixel ID Match': state.pixelIdMatch ? 'YES ✓' : 'NO ✗',
        '--- SCRIPTS ---': '---',
        'fbevents.js in DOM': state.scriptLoaded,
        'Script count': scripts.length,
        'Script URLs': Array.from(scripts).map(s => s.src),
        '--- ENVIRONMENT ---': '---',
        'REACT_APP_META_PIXEL_ID': process.env.REACT_APP_META_PIXEL_ID || 'not set',
        'User Agent': navigator.userAgent.substring(0, 100),
      };
      
      try {
        raw['localStorage consent'] = localStorage.getItem('smartballoon-cookie-consent');
      } catch (e) {
        raw['localStorage consent'] = 'blocked';
      }
      
      setRawData(raw);
      setDebugState(state);
      
    } catch (e) {
      console.error('[MetaDebug] Refresh error:', e);
      setErrors(prev => [...prev, { timestamp: new Date().toISOString(), message: e.message }]);
    }
    
    setLoading(false);
  }, []);

  // Setup interceptors on mount
  useEffect(() => {
    setupFbqInterceptor();
    setupNetworkMonitor();
    refreshDebugState();
    
    // Cleanup interval on unmount
    return () => {
      if (window.__metaDebugInterval) {
        clearInterval(window.__metaDebugInterval);
      }
    };
  }, [setupFbqInterceptor, setupNetworkMonitor, refreshDebugState]);

  // Force initialize pixel with full debugging
  const handleForceInit = () => {
    console.log('[MetaDebug] === FORCE INIT START ===');
    
    // Clear previous state
    setFbqCalls([]);
    setNetworkRequests([]);
    setErrors([]);
    
    // Set consent
    window.__metaPixelConsent = true;
    const consent = { essential: true, analytics: true, marketing: true, timestamp: new Date().toISOString() };
    try {
      localStorage.setItem('smartballoon-cookie-consent', JSON.stringify(consent));
    } catch (e) {
      console.log('[MetaDebug] localStorage error:', e);
    }
    window.__cookieConsent = consent;
    
    // Helper to track fbq call
    const trackFbqCall = (method, ...args) => {
      const callInfo = {
        timestamp: new Date().toISOString(),
        method,
        args,
        argsString: JSON.stringify([method, ...args]),
        source: 'direct'
      };
      setFbqCalls(prev => [...prev, callInfo]);
      
      if (method === 'init' && args[0]) {
        setDebugState(prev => ({
          ...prev,
          actualPixelId: args[0],
          pixelIdMatch: args[0] === META_PIXEL_ID
        }));
      }
    };
    
    // Check if fbevents.js is loaded
    const existingScript = document.querySelector('script[src*="fbevents.js"]');
    console.log('[MetaDebug] Existing fbevents script:', existingScript?.src);
    
    if (!existingScript) {
      console.log('[MetaDebug] Loading fbevents.js manually...');
      
      // First, create the fbq stub before loading script
      // This is exactly what Meta's snippet does
      const n = window.fbq = function() {
        n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
      };
      if (!window._fbq) window._fbq = n;
      n.push = n;
      n.loaded = false;
      n.version = '2.0';
      n.queue = [];
      
      // Load the script
      const script = document.createElement('script');
      script.src = FBEVENTS_URL;
      script.async = true;
      
      script.onload = () => {
        console.log('[MetaDebug] fbevents.js loaded!');
        n.loaded = true;
        
        // Now initialize
        try {
          console.log('[MetaDebug] Calling fbq("init", "' + META_PIXEL_ID + '")');
          window.fbq('init', META_PIXEL_ID);
          trackFbqCall('init', META_PIXEL_ID);
          window.__fbqInitialized = true;
          
          console.log('[MetaDebug] Calling fbq("track", "PageView")');
          window.fbq('track', 'PageView');
          trackFbqCall('track', 'PageView');
          
          setTimeout(refreshDebugState, 1500);
        } catch (err) {
          console.error('[MetaDebug] Error during init:', err);
          setErrors(prev => [...prev, { 
            timestamp: new Date().toISOString(), 
            message: 'Init error: ' + err.message 
          }]);
        }
      };
      
      script.onerror = (e) => {
        console.error('[MetaDebug] Failed to load fbevents.js:', e);
        setErrors(prev => [...prev, { 
          timestamp: new Date().toISOString(), 
          message: 'Failed to load fbevents.js - likely blocked by ad blocker or network' 
        }]);
        setTimeout(refreshDebugState, 500);
      };
      
      document.head.appendChild(script);
    } else {
      // Script already loaded, just init
      try {
        if (window.fbq) {
          console.log('[MetaDebug] fbq exists, calling init with:', META_PIXEL_ID);
          window.fbq('init', META_PIXEL_ID);
          trackFbqCall('init', META_PIXEL_ID);
          window.__fbqInitialized = true;
          
          console.log('[MetaDebug] Calling fbq("track", "PageView")');
          window.fbq('track', 'PageView');
          trackFbqCall('track', 'PageView');
          
          setTimeout(refreshDebugState, 1000);
        } else {
          console.error('[MetaDebug] Script in DOM but fbq not available!');
          setErrors(prev => [...prev, { 
            timestamp: new Date().toISOString(), 
            message: 'Script in DOM but fbq function not available' 
          }]);
        }
      } catch (err) {
        console.error('[MetaDebug] Error calling fbq:', err);
        setErrors(prev => [...prev, { 
          timestamp: new Date().toISOString(), 
          message: 'Error calling fbq: ' + err.message 
        }]);
      }
    }
    
    console.log('[MetaDebug] === FORCE INIT END ===');
  };

  // Test PageView
  const handleTestPageView = () => {
    console.log('[MetaDebug] Testing PageView...');
    
    if (!window.fbq) {
      setErrors(prev => [...prev, { 
        timestamp: new Date().toISOString(), 
        message: 'fbq not available - cannot track PageView' 
      }]);
      return;
    }
    
    try {
      const eventId = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      console.log('[MetaDebug] Calling fbq("track", "PageView") with eventID:', eventId);
      window.fbq('track', 'PageView', {}, { eventID: eventId });
    } catch (e) {
      console.error('[MetaDebug] PageView error:', e);
      setErrors(prev => [...prev, { timestamp: new Date().toISOString(), message: e.message }]);
    }
  };

  // Test direct network request to facebook.com/tr
  const handleTestDirectPixel = () => {
    console.log('[MetaDebug] Testing direct pixel request...');
    
    const pixelUrl = `https://www.facebook.com/tr/?id=${META_PIXEL_ID}&ev=PageView&dl=${encodeURIComponent(window.location.href)}&rl=&if=false&ts=${Date.now()}&cd[test]=debug`;
    
    // Test 1: Image pixel (standard method)
    const img = new Image();
    img.onload = () => {
      console.log('[MetaDebug] Direct pixel request SUCCESS (image loaded)');
      setNetworkRequests(prev => [...prev, {
        timestamp: new Date().toISOString(),
        type: 'direct-test-img',
        url: pixelUrl.substring(0, 200),
        method: 'GET',
        status: 'SUCCESS'
      }]);
    };
    img.onerror = (e) => {
      console.error('[MetaDebug] Direct pixel request FAILED (image):', e);
      // This often fails due to CORS/tracking protection but pixel may still work
      setErrors(prev => [...prev, { 
        timestamp: new Date().toISOString(), 
        message: 'Image pixel blocked - may be tracking protection (this is common, check fetch test below)' 
      }]);
    };
    img.src = pixelUrl;
    
    // Test 2: Fetch with no-cors (more reliable test)
    fetch(pixelUrl, { 
      method: 'GET', 
      mode: 'no-cors',  // Important: no-cors allows the request even without CORS headers
      credentials: 'omit'
    })
    .then(() => {
      console.log('[MetaDebug] Fetch request completed (no-cors mode)');
      setNetworkRequests(prev => [...prev, {
        timestamp: new Date().toISOString(),
        type: 'direct-test-fetch',
        url: pixelUrl.substring(0, 200),
        method: 'GET',
        status: 'SENT (no-cors)'
      }]);
    })
    .catch(err => {
      console.error('[MetaDebug] Fetch request FAILED:', err);
      setErrors(prev => [...prev, { 
        timestamp: new Date().toISOString(), 
        message: `Fetch failed: ${err.message} - Network/firewall blocking facebook.com` 
      }]);
    });
    
    // Test 3: Check if facebook.com is reachable at all
    fetch('https://www.facebook.com/favicon.ico', { 
      method: 'HEAD', 
      mode: 'no-cors',
      credentials: 'omit'
    })
    .then(() => {
      console.log('[MetaDebug] facebook.com is reachable');
      setNetworkRequests(prev => [...prev, {
        timestamp: new Date().toISOString(),
        type: 'connectivity-test',
        url: 'facebook.com',
        method: 'HEAD',
        status: 'REACHABLE'
      }]);
    })
    .catch(err => {
      console.error('[MetaDebug] facebook.com NOT reachable:', err);
      setErrors(prev => [...prev, { 
        timestamp: new Date().toISOString(), 
        message: `CRITICAL: facebook.com not reachable - firewall/DNS blocking? Error: ${err.message}` 
      }]);
    });
  };

  // Clear all
  const handleClearAll = () => {
    setFbqCalls([]);
    setNetworkRequests([]);
    setErrors([]);
    
    try {
      localStorage.removeItem('smartballoon-cookie-consent');
      sessionStorage.removeItem('smartballoon-cookie-consent');
    } catch (e) {
      console.log('[MetaDebug] Storage clear error:', e);
    }
    
    window.__metaPixelConsent = false;
    window.__fbqInitialized = false;
    window.__cookieConsent = null;
    
    setTimeout(refreshDebugState, 500);
  };

  const facebookTrRequests = networkRequests.filter(r => r.url.includes('facebook.com/tr'));

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="h-14 border-b border-slate-200 bg-white flex items-center px-6 justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate('/admin')}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Admin
          </Button>
          <h1 className="font-bold text-slate-900 flex items-center gap-2">
            <Bug className="w-5 h-5 text-purple-600" />
            Meta Pixel Debug
          </h1>
          <Badge variant="outline" className="font-mono text-xs">
            Pixel ID: {META_PIXEL_ID}
          </Badge>
        </div>
        <Button onClick={refreshDebugState} disabled={loading} size="sm">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
          <span className="ml-2">Refresh</span>
        </Button>
      </header>

      <main className="max-w-6xl mx-auto p-6 space-y-6">
        
        {/* Critical Status Overview */}
        <div className="grid md:grid-cols-4 gap-4">
          <Card className={debugState.marketingConsent ? 'border-green-200 bg-green-50' : 'border-red-200 bg-red-50'}>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2">
                <Cookie className="w-4 h-4" />
                Consenso Marketing
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-2">
                <StatusIcon ok={debugState.marketingConsent} />
                <span className={debugState.marketingConsent ? 'text-green-700 font-medium' : 'text-red-700 font-medium'}>
                  {debugState.marketingConsent ? 'Concesso' : 'Non concesso'}
                </span>
              </div>
            </CardContent>
          </Card>

          <Card className={debugState.scriptLoaded ? 'border-green-200 bg-green-50' : 'border-red-200 bg-red-50'}>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2">
                <Globe className="w-4 h-4" />
                fbevents.js
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-2">
                <StatusIcon ok={debugState.scriptLoaded} />
                <span className={debugState.scriptLoaded ? 'text-green-700 font-medium' : 'text-red-700 font-medium'}>
                  {debugState.scriptLoaded ? 'Caricato' : 'Non caricato'}
                </span>
              </div>
              {debugState.scriptLoadError && (
                <p className="text-xs text-red-600 mt-1">{debugState.scriptLoadError}</p>
              )}
            </CardContent>
          </Card>

          <Card className={debugState.fbqExists ? 'border-green-200 bg-green-50' : 'border-red-200 bg-red-50'}>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2">
                <Code className="w-4 h-4" />
                Funzione fbq
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-2">
                <StatusIcon ok={debugState.fbqExists} />
                <span className={debugState.fbqExists ? 'text-green-700 font-medium' : 'text-red-700 font-medium'}>
                  {debugState.fbqExists ? 'Disponibile' : 'Non trovata'}
                </span>
              </div>
              <p className="text-xs text-slate-500 mt-1">
                typeof: {typeof window.fbq}
              </p>
            </CardContent>
          </Card>

          <Card className={debugState.pixelIdMatch ? 'border-green-200 bg-green-50' : debugState.actualPixelId ? 'border-amber-200 bg-amber-50' : 'border-red-200 bg-red-50'}>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2">
                <Zap className="w-4 h-4" />
                Pixel ID Effettivo
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-2">
                <StatusIcon ok={debugState.pixelIdMatch} warning={debugState.actualPixelId && !debugState.pixelIdMatch} />
                <span className={`font-mono text-sm ${debugState.pixelIdMatch ? 'text-green-700' : 'text-red-700'}`}>
                  {debugState.actualPixelId || 'Non rilevato'}
                </span>
              </div>
              {!debugState.pixelIdMatch && debugState.actualPixelId && (
                <p className="text-xs text-amber-600 mt-1">
                  Atteso: {META_PIXEL_ID}
                </p>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Network Requests Monitor */}
        <Card className="border-blue-200">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-sm flex items-center gap-2">
              <Wifi className="w-4 h-4 text-blue-500" />
              Richieste Network a facebook.com/tr ({facebookTrRequests.length})
            </CardTitle>
            <Badge variant={facebookTrRequests.length > 0 ? 'default' : 'destructive'}>
              {facebookTrRequests.length > 0 ? 'Richieste rilevate' : 'Nessuna richiesta'}
            </Badge>
          </CardHeader>
          <CardContent>
            {facebookTrRequests.length === 0 ? (
              <div className="text-center py-4">
                <WifiOff className="w-8 h-8 text-red-400 mx-auto mb-2" />
                <p className="text-sm text-red-600 font-medium">
                  Nessuna richiesta a facebook.com/tr rilevata
                </p>
                <p className="text-xs text-slate-500 mt-1">
                  Questo indica che gli eventi non stanno raggiungendo Meta.
                  Possibili cause: ad blocker, errore di rete, script non caricato.
                </p>
              </div>
            ) : (
              <div className="space-y-2 max-h-40 overflow-y-auto">
                {facebookTrRequests.slice(-10).reverse().map((req, i) => (
                  <div key={i} className="flex items-start gap-2 p-2 bg-blue-50 rounded text-xs">
                    <Badge variant="outline" className="shrink-0">{req.type}</Badge>
                    <span className="text-slate-500">{req.timestamp}</span>
                    <span className="text-slate-700 font-mono truncate flex-1">{req.url}</span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* fbq Calls Monitor */}
        <Card className="border-purple-200">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-sm flex items-center gap-2">
              <Activity className="w-4 h-4 text-purple-500" />
              Chiamate fbq() Intercettate ({fbqCalls.length})
            </CardTitle>
            <Button variant="ghost" size="sm" onClick={() => setFbqCalls([])}>
              Clear
            </Button>
          </CardHeader>
          <CardContent>
            {fbqCalls.length === 0 ? (
              <p className="text-sm text-slate-500 text-center py-4">
                Nessuna chiamata fbq() intercettata. Clicca &quot;Force Init&quot; per iniziare.
              </p>
            ) : (
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {fbqCalls.slice(-15).reverse().map((call, i) => (
                  <div key={i} className="flex items-start gap-2 p-2 bg-purple-50 rounded text-xs">
                    <Badge variant={call.method === 'init' ? 'default' : 'secondary'}>
                      {call.method}
                    </Badge>
                    <span className="text-slate-500">{call.timestamp}</span>
                    <span className="text-slate-700 font-mono truncate flex-1">
                      {call.argsString}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* CONVERSION EVENTS LOG - Real-time tracking of all conversion events */}
        <Card className="border-2 border-orange-300 bg-orange-50">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-sm flex items-center gap-2 text-orange-800">
              <Zap className="w-4 h-4 text-orange-600" />
              Eventi Conversione ({conversionEvents.length})
            </CardTitle>
            <div className="flex gap-2">
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => {
                  if (window.__metaPixelEventLog) {
                    setConversionEvents([...window.__metaPixelEventLog]);
                  }
                }}
              >
                <RefreshCw className="w-3 h-3 mr-1" />
                Refresh
              </Button>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => {
                  setConversionEvents([]);
                  window.__metaPixelEventLog = [];
                }}
              >
                Clear
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {conversionEvents.length === 0 ? (
              <div className="text-center py-6">
                <p className="text-sm text-orange-700 font-medium">
                  Nessun evento di conversione registrato
                </p>
                <p className="text-xs text-orange-600 mt-2">
                  Registrati o fai login per vedere CompleteRegistration, Lead, Purchase...
                </p>
              </div>
            ) : (
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {conversionEvents.slice().reverse().map((event, i) => (
                  <div 
                    key={i} 
                    className={`p-3 rounded text-xs border ${
                      event.status === 'sent' 
                        ? 'bg-green-50 border-green-200' 
                        : event.status === 'blocked' 
                          ? 'bg-red-50 border-red-200' 
                          : 'bg-yellow-50 border-yellow-200'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <div className="flex items-center gap-2">
                        <Badge 
                          variant={event.status === 'sent' ? 'default' : 'destructive'}
                          className={event.status === 'sent' ? 'bg-green-600' : ''}
                        >
                          {event.eventName}
                        </Badge>
                        <span className={`font-medium ${
                          event.status === 'sent' ? 'text-green-700' : 'text-red-700'
                        }`}>
                          {event.status === 'sent' ? '✅ INVIATO' : event.status === 'blocked' ? '🚫 BLOCCATO' : '❌ ERRORE'}
                        </span>
                      </div>
                      <span className="text-slate-500">{event.timestamp}</span>
                    </div>
                    {event.reason && (
                      <p className="text-red-600 font-medium mb-1">Motivo: {event.reason}</p>
                    )}
                    {event.eventId && (
                      <p className="text-slate-600 font-mono">eventID: {event.eventId}</p>
                    )}
                    <p className="text-slate-500 mt-1">URL: {event.url}</p>
                    <details className="mt-1">
                      <summary className="cursor-pointer text-slate-500 hover:text-slate-700">
                        Dati evento
                      </summary>
                      <pre className="mt-1 p-2 bg-white rounded text-xs overflow-x-auto">
                        {JSON.stringify(event.eventData, null, 2)}
                      </pre>
                    </details>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Test Actions */}
        <Card>
          <CardHeader>
            <CardTitle className="text-sm">Azioni di Test</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-3">
              <Button onClick={handleForceInit} variant="default" size="sm">
                <Zap className="w-4 h-4 mr-2" />
                Force Init Pixel
              </Button>
              <Button onClick={handleTestPageView} variant="outline" size="sm">
                <Eye className="w-4 h-4 mr-2" />
                Test PageView
              </Button>
              <Button onClick={handleTestDirectPixel} variant="outline" size="sm">
                <Globe className="w-4 h-4 mr-2" />
                Test Connettività
              </Button>
              <Button onClick={handleClearAll} variant="destructive" size="sm">
                <XCircle className="w-4 h-4 mr-2" />
                Clear All
              </Button>
            </div>
            <p className="text-xs text-slate-500 mt-3">
              <strong>Test Connettività</strong>: Verifica se facebook.com e il pixel tracking sono raggiungibili dalla tua rete.
              Esegue 3 test: image pixel, fetch no-cors, e connectivity check.
            </p>
          </CardContent>
        </Card>

        {/* Errors */}
        <Card className="border-red-200">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-sm flex items-center gap-2">
              <AlertCircle className="w-4 h-4 text-red-500" />
              Errori ({errors.length})
            </CardTitle>
            <Button variant="ghost" size="sm" onClick={() => setErrors([])}>
              Clear
            </Button>
          </CardHeader>
          <CardContent>
            {errors.length === 0 ? (
              <p className="text-sm text-green-600 text-center py-2">
                ✓ Nessun errore rilevato
              </p>
            ) : (
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {errors.map((err, i) => (
                  <div key={i} className="p-2 bg-red-50 rounded text-sm text-red-700">
                    <span className="text-red-400 text-xs">{err.timestamp}</span>
                    <p className="font-mono">{err.message}</p>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Raw Data */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-sm flex items-center gap-2">
              <Code className="w-4 h-4" />
              Dati Grezzi Completi
            </CardTitle>
            <Button variant="ghost" size="sm" onClick={() => setShowRawData(!showRawData)}>
              {showRawData ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              <span className="ml-2">{showRawData ? 'Nascondi' : 'Mostra'}</span>
            </Button>
          </CardHeader>
          {showRawData && (
            <CardContent>
              <pre className="bg-slate-900 text-green-400 p-4 rounded-lg text-xs overflow-x-auto whitespace-pre-wrap">
                {JSON.stringify(rawData, null, 2)}
              </pre>
            </CardContent>
          )}
        </Card>

        {/* Troubleshooting Guide */}
        <Card className="bg-amber-50 border-amber-200">
          <CardHeader>
            <CardTitle className="text-sm text-amber-800 flex items-center gap-2">
              <AlertCircle className="w-4 h-4" />
              Guida al Troubleshooting
            </CardTitle>
          </CardHeader>
          <CardContent className="text-sm text-amber-900 space-y-3">
            <div>
              <p className="font-medium text-red-700">⚠️ Se &quot;Direct pixel request FAILED&quot;:</p>
              <ul className="list-disc list-inside text-xs ml-2 mt-1 space-y-1">
                <li><strong>Firewall aziendale</strong> - Molte reti aziendali bloccano facebook.com/tr</li>
                <li><strong>Pi-hole / DNS blocker</strong> - Controlla se usi blocchi DNS a livello di rete</li>
                <li><strong>Browser anti-tracking</strong> - Firefox Enhanced Tracking Protection, Brave Shields</li>
                <li><strong>Estensioni browser</strong> - uBlock Origin, Privacy Badger, Ghostery</li>
                <li>Prova da un dispositivo mobile su rete 4G/5G per escludere blocchi di rete</li>
              </ul>
            </div>
            <div>
              <p className="font-medium">Se &quot;fbevents.js&quot; non è caricato:</p>
              <ul className="list-disc list-inside text-xs ml-2 mt-1 space-y-1">
                <li>connect.facebook.net è bloccato dalla tua rete</li>
                <li>Content Security Policy del sito potrebbe bloccare script esterni</li>
                <li>Disabilita temporaneamente ad blockers per testare</li>
              </ul>
            </div>
            <div>
              <p className="font-medium">Se chiamate fbq OK ma Meta Events Manager vuoto:</p>
              <ul className="list-disc list-inside text-xs ml-2 mt-1 space-y-1">
                <li>Le richieste partono ma vengono bloccate a livello di rete</li>
                <li>Attendi 5-10 minuti - Meta Events Manager ha ritardo</li>
                <li>Verifica che il Pixel ID sia corretto nel tuo Business Manager</li>
                <li><strong>Testa da rete diversa</strong> (hotspot mobile)</li>
              </ul>
            </div>
            <div>
              <p className="font-medium">Se il Pixel ID non corrisponde:</p>
              <ul className="list-disc list-inside text-xs ml-2 mt-1 space-y-1">
                <li>Verifica REACT_APP_META_PIXEL_ID nel file .env</li>
                <li>Riavvia il server di sviluppo dopo modifiche al .env</li>
              </ul>
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default MetaDebugPage;
